import React, { useState, forwardRef, useImperativeHandle, useEffect } from "react";
import { colors, fonts, glyphs } from "../../Styles";
import { NewInputHandles, ValueSteps } from "../../Definitions";
import { debounce } from "lodash";
import { styled } from "styletron-react";

// #region | Styled Input
export const StyledInput = styled("input", () => ({
  height: "30px",
  width: "60px",
  boxSizing: "border-box",
  borderRadius: "10px",
  border: "none",
  backgroundColor: colors.blueDarkSecondary,
  outline: "none",
  fontFamily: "Inter Flex",
  fontWeight: "300",
  fontSize: "14px",
  lineHeight: "normal",
  margin: "0",
  textTransform: "none",
  padding: "6px 10px 6px 10px",
  color: colors.greyLightTertiary,
  "::placeholder": {
    color: colors.greyLightTertiary,
    filter: "opacity(0.5)"
  },
  "::-webkit-inner-spin-button": {
    "-webkit-appearance": "none"
  },
  "::-webkit-outer-spin-button": {
    "-webkit-appearance": "none"
  },
  "input[type=number]": {
    "-moz-appearance": "textfield"
  },
  ":disabled": {
    color: colors.greyLightTertiary + "ab",
    cursor: "not-allowed",
    backgroundColor: colors.blueDarkSecondary + "52"
  }
}));
// #endregion

interface newInputProps {
  onChange?: (e: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  text?: string;
  isDisabled?: boolean;
  valueOverrides?: {
    defaultValue?: string | number;
    valueStep?: ValueSteps;
    minValueNumber?: number;
    maxValueNumber?: number;
  };
}
/**
 * NewInput
 * @param onChange
 * @param onBlur
 * @param text
 * @param placeholder The placeholder text displayed in the input field.
 * @param isDisabled Disables the input field.
 * @param valueOverrides Default values and settings for the input field.
 * @returns A styled input component with optional start enhancer and clear functionality.
 */
const _NewInput: React.ForwardRefRenderFunction<NewInputHandles, newInputProps> = ({ onChange, onBlur, placeholder, text, isDisabled = false, valueOverrides }: newInputProps, ref) => {
  const [isDecreasingHovered, setIsDecreasingHovered] = useState<boolean>(false); 
  const [isIncreasingHovered, setIsIncreasingHovered] = useState<boolean>(false); 
  const [, setError] = useState(false)
  const [shouldTriggerSpinnerBlur, setShouldTriggerSpinnerBlur] = useState(false);
  const [inputValue, setInputValue] = useState((valueOverrides?.defaultValue || valueOverrides?.defaultValue === 0) ? valueOverrides.defaultValue : "" );
  const _placeholder = placeholder ? placeholder + "..." : "...";

  useEffect(() => {
    if (shouldTriggerSpinnerBlur && onBlur) {
      setShouldTriggerSpinnerBlur(false);
      onBlur()
    }
  },[onBlur, shouldTriggerSpinnerBlur])

  const debouncedSetTriggerBlurState = debounce(() => {
    setShouldTriggerSpinnerBlur(true);
  }, 200)

  useEffect(() => { // TODO Could be removed if loading states were introduced through out the web panel 
    if (valueOverrides && (valueOverrides.defaultValue || valueOverrides.defaultValue === 0)) {
      setInputValue(valueOverrides.defaultValue)
    }
  },[valueOverrides])

  useImperativeHandle(ref, () => ({
    setError(state=true) {
      setError(state)
    },
    value: inputValue,
    setValue: (e: string | number) => { setInputValue(e as string); }
  }));

  // #region | Decreasing and increasing buttons
  const decreasingSpinner = (
    <button 
      onClick={() => {
        const newValue = Number(inputValue) - 1;
        if (valueOverrides?.minValueNumber !== undefined && newValue < valueOverrides.minValueNumber) return;
        setInputValue(newValue);
        if (onChange) {onChange(String(newValue))}
        if (onBlur) {debouncedSetTriggerBlurState()}
      }}
      onMouseEnter={() => setIsDecreasingHovered(true)}
      onMouseLeave={() => setIsDecreasingHovered(false)}
      style={{transition: "fill 0.3s ease", cursor: "pointer", height: "10px", width: "20px", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: colors.transparent, border: "none", padding: "0px"}}
    >
      <glyphs.ArrowSmall fillColor={isDecreasingHovered ? colors.greyLightTertiary : colors.blueDarkPrimary} rotate={90} size={"10px"} />
    </button>
  )

  const increasingSpinner = (
    <button 
    onClick={() => {
      const current = Number(inputValue);
      const max = valueOverrides?.maxValueNumber ?? 2147483647;
      if (current >= max) return;
    
      const newValue = current + 1;
      setInputValue(newValue);
      if (onChange) onChange(String(newValue));
      if (onBlur) debouncedSetTriggerBlurState();
    }}
      onMouseEnter={() => setIsIncreasingHovered(true)}
      onMouseLeave={() => setIsIncreasingHovered(false)}
      style={{transition: "fill 0.3s ease", cursor: "pointer", height: "10px", width: "20px", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: colors.transparent, border: "none", padding: "0px"}}
    >
      <glyphs.ArrowSmall fillColor={isIncreasingHovered ? colors.greyLightTertiary : colors.blueDarkPrimary} rotate={-90} size={"10px"} />
    </button>
  )

  const spinBtns = ( 
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "2px"}}>
      {increasingSpinner}
      {decreasingSpinner}
    </div> 
  )
  // #endregion

  // #region | ValueSteps
  let valueStep = 1;

  switch (valueOverrides?.valueStep) {
    case ValueSteps.floats:
      valueStep = 0.1;
      break;
    case ValueSteps.integers:
      default:
      valueStep = 1;
      break;
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (valueOverrides?.valueStep === ValueSteps.integers || valueOverrides?.valueStep === undefined) {
      if (e.key === "." || e.key === "," || e.key === "Decimal") {
        e.preventDefault();
      }
    }
  };
  // #endregion

  return (
    <div style={{ width: "60px", display: "flex", flexDirection: "row-reverse" as const, alignItems: "center", justifyContent: "start", gap: "10px" }}>
      {text && <p style={{...fonts.paragraph, ...fonts.fontSize3, color: colors.greyLightTertiary, whiteSpace: "nowrap"}}>{text}</p>}
      <div style={{ position: "relative" }}>
        <StyledInput
          value={inputValue}
          onKeyDown={handleKeyDown}
          step={valueStep}
          onChange={e => {
            let newValue = e.target.value;
              const num = Number(newValue);
              if (!isNaN(num)) {
                if (valueOverrides?.maxValueNumber !== undefined && num > valueOverrides.maxValueNumber) {
                  newValue = valueOverrides.maxValueNumber.toString();
                }
                if (valueOverrides?.minValueNumber !== undefined && num < valueOverrides.minValueNumber) {
                  newValue = valueOverrides.minValueNumber.toString();
                }
              }
            setInputValue(newValue);
            if (onChange) onChange(newValue);
          }}
          onBlur={onBlur}
          placeholder={_placeholder}
          disabled={isDisabled}
          defaultValue={valueOverrides?.defaultValue}
          type={"number"}
          min={valueOverrides?.minValueNumber}
          max={valueOverrides?.maxValueNumber}
        />
        
        {/* NUMBER FIELD (arrows) */}
        <div style={{ position: "absolute", zIndex: "99",  height: "22px", top: "50%", transform: "translateY(-50%)", right: "-6px", display: "grid", gridAutoFlow: "column", gridAutoColumns: "1fr", padding: "4px 8px", alignItems: "center" }}>
          {!isDisabled ? (<>{spinBtns}</>):<></>}
        </div>
      </div>
    </div>
  );
};
const NewInput = forwardRef(_NewInput);
export default NewInput;